import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { environment } from '../../environments/environment';
import { catchError, throwError, from, switchMap, lastValueFrom } from 'rxjs';
import { ReloadAppStateFromInit, SetSessionExpired } from '../store/app/app.actions';
import { AuthState } from '../store/auth/auth.state';
import { RefreshJWT, JwtHelper } from '@bo/ng-auth';
import { Mutex } from 'async-mutex';

// Create a single, global mutex lock instance for the interceptor
const mutex = new Mutex();
const jwtHelper = new JwtHelper();

export const apiInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const store = inject(Store);

  const addToken = (request: HttpRequest<unknown>, jwt: string) => {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${jwt}` },
    });
  };

  /**
   * Helper function that securely fetches a new token using the async-mutex lock.
   * Only one request can execute this at a time. The rest wait in line.
   */
  // Accept the failed token as an optional argument
  const getFreshToken = async (failedToken: string | null = null): Promise<string | null> => {
    return await mutex.runExclusive(async () => {
      // If the session is already dead, abort.
      // Use direct state access instead of AppSelectors to avoid circular dependency
      const sessionExpired = store.selectSnapshot(
        (state: { app?: { sessionExpired?: boolean } }) => state.app?.sessionExpired,
      );
      if (sessionExpired) {
        return null;
      }

      const currentToken = store.selectSnapshot(AuthState.token);

      if (failedToken) {
        // The server rejected our token.
        // We ONLY skip the refresh if the token actually changed while we were waiting in line
        if (currentToken !== failedToken) {
          return currentToken;
        }
      } else if (currentToken && jwtHelper.isTokenValid(currentToken)) {
        return currentToken;
      }

      try {
        await lastValueFrom(store.dispatch(new RefreshJWT()));

        const newToken = store.selectSnapshot(AuthState.token);
        if (!newToken) {
          store.dispatch(new SetSessionExpired(true));
          return null;
        }

        // After JWT refresh, reload app state to refresh user/business/subscription/accounting data
        // This ensures data is current after the user has been away from the app
        store.dispatch(new ReloadAppStateFromInit());

        return newToken;
      } catch (error) {
        // Only set session expired for auth errors, not network errors
        if (error instanceof HttpErrorResponse) {
          // 401 = token invalid/expired (real auth failure)
          // 403 = forbidden (real auth failure)
          if (error.status === 401 || error.status === 403) {
            store.dispatch(new SetSessionExpired(true));
          }
          // For network errors (0, 500, 502, 503, 504), don't mark session expired
          // This prevents false positives during temporary network issues
        } else {
          // Unknown error type, mark session expired to be safe
          store.dispatch(new SetSessionExpired(true));
        }
        throw error;
      }
    });
  };

  if (req.url.startsWith(environment.apiRoot) && !req.url.includes('refresh') && !req.url.includes('/auth/token')) {
    const token = store.selectSnapshot(AuthState.token);

    // Proactively do a mathematical check if the token has expired locally
    if (token && !jwtHelper.isTokenValid(token)) {
      // Pass null here because the token just expired naturally; the server hasn't rejected it yet.
      return from(getFreshToken(null)).pipe(
        switchMap((newToken) => {
          if (!newToken) {
            // Throw a synthetic 401 HttpErrorResponse
            const authError = new HttpErrorResponse({
              error: 'Token refresh failed',
              status: 401,
              statusText: 'Unauthorized',
              url: req.url,
            });
            return throwError(() => authError);
          }

          // Retry the request with the new token
          return next(addToken(req, newToken)).pipe(
            catchError((err: HttpErrorResponse) => handleReactive401(err, req, next, addToken, getFreshToken)),
          );
        }),
      );
    }

    // Token is valid locally, attach it
    if (token) {
      req = addToken(req, token);
    }
  }

  // Execute standard HTTP request
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => handleReactive401(error, req, next, addToken, getFreshToken)),
  );
};

/**
 * Fallback handler: If Django rejects the token even though it looked valid locally.
 */
function handleReactive401(
  error: HttpErrorResponse,
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  addToken: (request: HttpRequest<unknown>, jwt: string) => HttpRequest<unknown>,
  getFreshToken: (failedToken: string | null) => Promise<string | null>,
) {
  if (error?.status === 401 && error.error?.code === 'token_not_valid' && !req.url.includes('refresh')) {
    const failedToken = req.headers.get('Authorization')?.replace('Bearer ', '') || null;

    // Pass the failed token into the mutex so it knows to ignore it
    return from(getFreshToken(failedToken)).pipe(
      switchMap((newToken) => {
        if (!newToken) return throwError(() => error);
        return next(addToken(req, newToken));
      }),
      catchError((refreshErr) => throwError(() => refreshErr)),
    );
  }
  return throwError(() => error);
}
